import { createFileRoute, Outlet, redirect } from "@tanstack/react-router";
import { z } from "zod";
import { getBookingsSyncedSearchParams } from "@/bookings/Bookings/utils";
import { BOOKINGS_SORT_FIELDS } from "@/bookings/BookingsTable/constants.ts";
import { type BookingStatus } from "@/bookings/BookingStatusBadge";
import { getStoredPageSize } from "@/hooks/use-page-size-preference";
import { getStoredSortPreference } from "@/hooks/use-sort-preference";

export type BookingFilters = z.infer<typeof bookingsFilterSchema>;

export const bookingsFilterSchema = z.object({
  lotNumber: z.string().optional().catch(undefined),
  accountNumber: z.string().optional().catch(undefined),
  name: z.string().optional().catch(undefined),
  dateOpenedBefore: z.string().date().optional().catch(undefined),
  dateOpenedAfter: z.string().date().optional().catch(undefined),
  status: z
    .array(
      z.enum([
        "PLANNED",
        "DRAFT",
        "BOOKED",
        "SHIPPED",
        "AWAITING_PAYMENT",
        "PAID",
        "CANCELLED",
        "ARCHIVED",
        "CLOSED",
      ] as const satisfies Array<BookingStatus>),
    )
    .optional()
    .catch(undefined),
  sourceIds: z.array(z.string()).optional().catch(undefined),
  destinationIds: z.array(z.string()).optional().catch(undefined),
  sortField: z.enum(BOOKINGS_SORT_FIELDS).optional().catch(undefined),
  sortDesc: z.boolean().optional().catch(undefined),
  pageSize: z.enum(["10", "20", "50", "100"]).optional().catch(undefined),
});

export const Route = createFileRoute("/(authed)/_layout/bookings")({
  component: RouteComponent,
  validateSearch: bookingsFilterSchema,
  beforeLoad: ({ search, location }) => {
    // Only sync for the main bookings route, not child routes (like /bookings/$bookingId)
    if (location.pathname !== "/bookings") {
      return;
    }

    const storedPageSize = getStoredPageSize("bookingsTable");
    const storedSort = getStoredSortPreference("bookingsTable");

    const syncedSearch = getBookingsSyncedSearchParams({
      search,
      storedPageSize,
      storedSort,
      validSortFields: BOOKINGS_SORT_FIELDS,
    });

    // Only apply default status if this looks like an initial visit (no filters at all)
    const hasAnyFilters =
      search.name ||
      search.lotNumber ||
      search.accountNumber ||
      search.dateOpenedAfter ||
      search.dateOpenedBefore ||
      search.status;
    const shouldApplyDefaultStatus = !hasAnyFilters;
    const defaultStatus: BookingStatus[] = [
      "PLANNED",
      "BOOKED",
      "SHIPPED",
      "AWAITING_PAYMENT",
      "PAID",
      "DRAFT",
      "CANCELLED",
    ];

    if (syncedSearch || shouldApplyDefaultStatus) {
      redirect({
        to: "/bookings",
        search: {
          ...(syncedSearch || search),
          ...(shouldApplyDefaultStatus && { status: defaultStatus }),
        },
        throw: true,
      });
    }
  },
  loader: () => {
    return {
      crumb: "Bookings",
    };
  },
});

function RouteComponent() {
  return <Outlet />;
}
